/**
 * StalenessIndicator Component
 *
 * Displays a warning when intent patterns may be outdated relative to the
 * agent's context (system prompt, documents, URLs).
 *
 * Validates: Requirements 8.1, 8.2, 8.3, 8.5
 * - Requirement 8.1: WHEN the agent's systemPrompt is modified after the last pattern generation,
 *   THE Console SHALL display a warning indicator.
 * - Requirement 8.2: WHEN a contextDocument is added or removed after the last pattern generation,
 *   THE Console SHALL display a warning indicator.
 * - Requirement 8.3: THE warning indicator SHALL include the text
 *   "Los patrones de intent pueden estar desactualizados" with the date of last generation.
 * - Requirement 8.5: WHEN the operator regenerates patterns, THE System SHALL clear the warning indicator.
 */

import { Badge } from '../../design-system';
import { tokens } from '../../design-system/tokens';

// ============================================
// TYPES
// ============================================

export interface StalenessIndicatorProps {
  /** Whether patterns are stale */
  stale: boolean;
  /** ISO8601 timestamp of last pattern generation */
  generatedAt: string | null;
  /** ISO8601 timestamp of last context change */
  lastContextChange?: string | null;
}

// ============================================
// ICONS
// ============================================

function IconWarning() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden="true"
    >
      <path
        d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <line x1="12" y1="9" x2="12" y2="13" strokeLinecap="round" strokeLinejoin="round" />
      <line x1="12" y1="17" x2="12.01" y2="17" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// ============================================
// HELPERS
// ============================================

function formatDate(isoDate: string): string {
  try {
    const date = new Date(isoDate);
    return date.toLocaleDateString('es-ES', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return isoDate;
  }
}

// ============================================
// MAIN COMPONENT
// ============================================

/**
 * StalenessIndicator — Warning badge when patterns may be outdated.
 *
 * Shows a prominent warning message when the agent's context has changed
 * since the last pattern generation, indicating patterns should be regenerated.
 *
 * @example
 * ```tsx
 * <StalenessIndicator
 *   stale={true}
 *   generatedAt="2025-01-15T10:30:00.000Z"
 * />
 * ```
 */
export function StalenessIndicator({ stale, generatedAt }: StalenessIndicatorProps) {
  if (!stale) return null;

  return (
    <div
      role="alert"
      aria-live="polite"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: tokens.spacing[3],
        padding: `${tokens.spacing[3]} ${tokens.spacing[4]}`,
        borderRadius: tokens.borderRadius.md,
        backgroundColor: tokens.colors.warning.light,
        border: `1px solid ${tokens.colors.warning.main}`,
        marginBottom: tokens.spacing[4],
      }}
    >
      <div
        style={{
          color: tokens.colors.warning.dark,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <IconWarning />
      </div>
      <div style={{ flex: 1 }}>
        <p
          style={{
            margin: 0,
            fontSize: tokens.typography.fontSize.sm,
            fontWeight: tokens.typography.fontWeight.medium,
            color: tokens.colors.warning.dark,
            lineHeight: tokens.typography.lineHeight.normal,
          }}
        >
          La configuración del agente cambió desde la última generación. Las reglas de detección podrían no reflejar el contexto actual.
        </p>
        {generatedAt && (
          <p
            style={{
              margin: 0,
              marginTop: tokens.spacing[1],
              fontSize: tokens.typography.fontSize.xs,
              color: tokens.colors.warning.dark,
              opacity: 0.8,
            }}
          >
            Última generación: {formatDate(generatedAt)}
          </p>
        )}
      </div>
      <Badge variant="warning" size="sm">
        Desactualizado
      </Badge>
    </div>
  );
}

export default StalenessIndicator;
